import {
  ColumnDefinition,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiDataTableComponent,
  OuiDividerComponent,
  OuiGridComponent,
  OuiPaginatorComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiRowComponent,
  OuiStackComponent,
  OuiSymbolComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { Component, computed, effect, inject, signal, TemplateRef, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { InputComponent } from '../../components/input/input.component';
import { MultiSelectDropdownComponent } from '../../components/multi-select-dropdown/multi-select-dropdown';
import { RmaStatusCountsRequest } from '../../models/rma.models';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { DeviceCareCenterService } from '../../services/device-care-center/device-care-center.service';
import { RmaService } from '../../services/rma/rma.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { RmaSummaryRow } from '../../services/rma/rma.mapper';
import { BasePageComponent } from '../base/base.component';



@Component({
  selector: 'app-rma-summary',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiButtonComponent,
    OuiDataTableComponent,
    OuiDividerComponent,
    OuiGridComponent,
    OuiPaginatorComponent,
    OuiRowComponent,
    OuiStackComponent,
    OuiSymbolComponent,
    OuiTextComponent,
    InputComponent,
    MultiSelectDropdownComponent,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './rma-summary.component.html',
  styleUrls: ['./rma-summary.component.css'],
})
export class RmaSummaryPage extends BasePageComponent {
    rmaNumberCell = viewChild<TemplateRef<never>>('rmaNumberCell');
  readonly statusCell = viewChild<TemplateRef<never>>('statusCell');
    readonly complianceCell = viewChild<TemplateRef<never>>('complianceCell');

  private readonly ALL_OPTION_VALUE = '__all__';
  private readonly salesChannelService = inject(SalesChannelService);
  private readonly deviceCareCenterService = inject(DeviceCareCenterService);
  private readonly rmaService = inject(RmaService);
  private readonly warehouseService = inject(WarehouseService);

  readonly channelOptions = computed(() =>
    this.salesChannelService.salesChannelOptions()
  );

  readonly dccOptions = computed(() =>
    this.deviceCareCenterService.deviceCareCenters
      .value()
      .map((dcc) => ({ label: dcc.name, value: dcc.code }))
  );

  readonly warehouseOptions = computed(() =>
    this.warehouseService.warehouseOptions().map((warehouse) => ({
      label: warehouse.value,
      value: warehouse.value,
    }))
  );

  searchForm = new FormGroup({
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    rmaNumber: new FormControl(''),
    rmaType: new FormControl<string[]>(['__all__']),
    rmaStatus: new FormControl<string[]>(['__all__']),
    orderNumber: new FormControl(''),
    channel: new FormControl<string[]>(['__all__']),
    compliance: new FormControl<string[]>(['__all__']),
    dcc: new FormControl<string[]>(['__all__']),
    warehouseCode: new FormControl<string[]>(['__all__']),
  });

  searchFormValue = toSignal(this.searchForm.valueChanges);

  endDateMinValue = computed(() => {
    const valueChanges = this.searchFormValue();
    const startDate = valueChanges?.startDate;
    return typeof startDate === 'string' && startDate.length > 0
      ? startDate
      : undefined;
  });

  readonly selectOptions = [
    { label: 'Regular', value: 'Regular' },
    { label: 'Credit Only', value: 'Credit Only' },
  ];

  readonly rmaStatusOptions = [
    { label: 'Pending Approval', value: 'Pending Approval' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Rejected', value: 'Rejected' },
    { label: 'Received', value: 'Received' },
    { label: 'Hold', value: 'Hold' },
    { label: 'Finance Processing', value: 'Finance Processing' },
    { label: 'Under Review', value: 'Under Review' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Closed', value: 'Closed' },
  ];

  readonly complianceOptions = [  
    { label: 'Compliant', value: 'Compliant' },
    { label: 'Non Compliant', value: 'Non Compliant' },
  ];

  readonly statusTiles = [
    { key: 'underReview', label: 'Under Review', status: 'Under Review' },
    { key: 'approved', label: 'Approved', status: 'Approved' },
    { key: 'rejected', label: 'Rejected', status: 'Rejected' },
  ];

  readonly activeStatusTile = signal<string | null>(null);
  readonly statusTileCounts = signal<Record<string, number>>({});
  readonly isStatusTileCountsLoading = signal(false);
  private statusTileCountsRequestId = 0;
  private lastRegionId?: number;

  rmaSummaryColumns!: ColumnDefinition<RmaSummaryRow>[];

  rmaSummaryDatasource = new OuiDatasource<RmaSummaryRow>((request) =>
    this.rmaService.rmaDataLoader({
      ...request,
      sortColumn: request.sortColumn === 'date' ? 'Date' : request.sortColumn || 'Date',
      sortDirection:
        request.sortDirection && request.sortDirection !== 'indet'
          ? request.sortDirection
          : 'desc',
    }),
  );

  constructor() {
    super();

    this.rmaSummaryDatasource.sortRecords({
      column: 'date',
      direction: 'desc',
    });

    effect(() => {
      this.rmaSummaryColumns = [
        {
          columnDef: 'rmaNumber',
          header: 'RMA#',
          template: this.rmaNumberCell(),
        },
        { columnDef: 'rmaType', header: 'Type' },
        { columnDef: 'order', header: 'Order Number' },
        { columnDef: 'shipmentId', header: 'Shipment Number' },
        { columnDef: 'dcc', header: 'DCC' },
        { columnDef: 'warehouse', header: 'WH' },
        {
          columnDef: 'status',
          header: 'Status',
          template: this.statusCell(),
        },
        {
          columnDef: 'compliance',
          header: 'Compliance',
          template: this.complianceCell(),
        },
        { columnDef: 'channel', header: 'Channel' },
        { columnDef: 'createdBy', header: 'Created by' },
        { columnDef: 'date', header: 'Date' },
        { columnDef: 'lastUpdatedDate', header: 'Last Update Date' },
      ];
    });

    effect(() => {
      const regionId = this.regions.selectedRegionId();
      if (!regionId) {
        return;
      }

      if (this.lastRegionId == null) {
        this.lastRegionId = regionId;
        void this.refreshStatusTileCounts(this.getStatusCountsPayload());
        return;
      }

      if (this.lastRegionId === regionId) {
        return;
      }

      this.lastRegionId = regionId;
      this.refreshRmaSummaryForRegion();
      void this.refreshStatusTileCounts(this.getStatusCountsPayload());
    });
  }

  openRmaDetails(rmaId: number) {
    this.router.navigate(['/rma-management/summary', rmaId]);
  }

  searchRmas() {
    const selectedStatuses = this.searchForm.controls.rmaStatus.value;
    this.activeStatusTile.set(this.resolveActiveStatusTile(selectedStatuses));

    this.rmaSummaryDatasource.setFilter(this.getCombinedFilter());
    void this.refreshStatusTileCounts(this.getStatusCountsPayload());
  }

  resetSearch() {
    this.activeStatusTile.set(null);
    this.searchForm.reset({
      startDate: '',
      endDate: '',
      rmaNumber: '',
      rmaType: ['__all__'],
      rmaStatus: ['__all__'],
      orderNumber: '',
      channel: ['__all__'],
      compliance: ['__all__'],
      dcc: ['__all__'],
      warehouseCode: ['__all__'],
    });

    this.rmaSummaryDatasource.setFilter({});
    void this.refreshStatusTileCounts(this.getStatusCountsPayload());
  }

  applyStatusTileFilter(tileKey: string) {
    const tile = this.statusTiles.find((current) => current.key === tileKey);
    if (!tile || this.isStatusTileDisabled(tileKey)) {
      return;
    }

    this.searchForm.controls.rmaStatus.setValue([this.ALL_OPTION_VALUE]);
    this.activeStatusTile.set(tile.key);
    this.rmaSummaryDatasource.setFilter(this.getCombinedFilter());
    void this.refreshStatusTileCounts(this.getStatusCountsPayload());
  }

  private getCombinedFilter() {
    const filter = this.getSearchFilter();
    const activeTileKey = this.activeStatusTile();

    if (!activeTileKey) {
      return filter;
    }

    const tile = this.statusTiles.find((current) => current.key === activeTileKey);
    if (!tile) {
      return filter;
    }

    return {
      ...filter,
      status: [tile.status],
    };
  }

  getStatusTileCount(tileKey: string): number {
    return this.statusTileCounts()[tileKey] ?? 0;
  }

  isStatusTileDisabled(tileKey: string): boolean {
    return this.getStatusTileCount(tileKey) === 0;
  }

  getStatusTileIcon(tileKey: string): string {
    switch (tileKey) {
      case 'underReview':
        return 'schedule';
      case 'approved':
        return 'task_alt';
      case 'rejected':
        return 'close';
      default:
        return 'schedule';
    }
  }

  private getSearchFilter() {
    const {
      startDate,
      endDate,
      rmaNumber,
      rmaType,
      rmaStatus,
      orderNumber,
      channel,
      compliance,
      dcc,
      warehouseCode,
    } = this.searchForm.getRawValue();

    return {
      rmaNumber: rmaNumber || '',
      orderNumber: orderNumber || '',
      startDate: startDate || '',
      endDate: endDate || '',
      rmaType: this.toApiValues(rmaType, this.selectOptions),
      status: this.toApiValues(rmaStatus, this.rmaStatusOptions),
      salesChannelCode: this.toApiValues(channel, this.channelOptions()),
      rmaComplianceStatus: this.toApiValues(compliance, this.complianceOptions),
      dccCode: this.toApiValues(dcc, this.dccOptions()),
      warehouseCode: this.toApiValues(warehouseCode, this.warehouseOptions()),
      erpRmaNumber: '',
      atbId: null,
      shipmentNumber: '',
    };
  }

  private getStatusCountsPayload(): RmaStatusCountsRequest {
    const filter = this.getSearchFilter();

    return {
      regionId: this.regions.selectedRegionId() ?? 0,
      rmaNumber: filter.rmaNumber,
      erpRmaNumber: '',
      atbId: null,
      rmaType: filter.rmaType,
      status: filter.status,
      startDate: filter.startDate,
      endDate: filter.endDate,
      rmaComplianceStatus: filter.rmaComplianceStatus,
      orderNumber: filter.orderNumber,
      salesChannelCode: filter.salesChannelCode,
      dccCode: filter.dccCode,
      warehouseCode: filter.warehouseCode,
      shipmentNumber: '',
    };
  }

  private refreshRmaSummaryForRegion() {
    this.rmaSummaryDatasource.setFilter(this.getCombinedFilter());
  }

  private toApiValues(
    values: string[] | null,
    _options: Array<{ value: unknown }> = [],
  ): string[] {
    if (!values || values.length === 0) return [];

    // If 'All' was explicitly selected, send no filter
    if (values.includes(this.ALL_OPTION_VALUE)) return [];

    return values.filter((value) => value !== this.ALL_OPTION_VALUE);
  }

  private normalizeStatus(status: string): string {
    return status.replace(/[^a-zA-Z]/g, '').toLowerCase();
  }

  private resolveActiveStatusTile(
    selectedStatuses: string[] | null,
  ): string | null {
    const selected = this.toApiValues(selectedStatuses, this.rmaStatusOptions);

    if (selected.length !== 1) {
      return null;
    }

    return (
      this.statusTiles.find(
        (tile) => this.normalizeStatus(tile.status) === this.normalizeStatus(selected[0]),
      )?.key ?? null
    );
  }

  private resolveSelectedStatusTileKeys(selectedStatuses: string[] | null): string[] {
    const selected = this.toApiValues(selectedStatuses, this.rmaStatusOptions);
    if (selected.length === 0) {
      return [];
    }

    const selectedNormalized = new Set(selected.map((status) => this.normalizeStatus(status)));
    return this.statusTiles
      .filter((tile) => selectedNormalized.has(this.normalizeStatus(tile.status)))
      .map((tile) => tile.key);
  }

  private async refreshStatusTileCounts(payload: RmaStatusCountsRequest) {
    const requestId = ++this.statusTileCountsRequestId;
    this.isStatusTileCountsLoading.set(true);

    try {
      const counts = await this.rmaService.getStatusCounts(payload);

      if (requestId !== this.statusTileCountsRequestId) {
        return;
      }

      const countMap = counts.reduce<Record<string, number>>((acc, item) => {
        acc[this.normalizeStatus(item.status)] = item.count;
        return acc;
      }, {});

      const selectedTileKeys = this.resolveSelectedStatusTileKeys(
        this.searchForm.controls.rmaStatus.value,
      );
      const hasSelectedStatusFilter = Array.isArray(payload.status) && payload.status.length > 0;

      const baseCounts = {
        underReview: countMap['underreview'] ?? 0,
        approved: countMap['approved'] ?? 0,
        rejected: countMap['rejected'] ?? 0,
      };

      if (selectedTileKeys.length > 0) {
        const selectedSet = new Set(selectedTileKeys);
        this.statusTileCounts.set({
          underReview: selectedSet.has('underReview') ? baseCounts.underReview : 0,
          approved: selectedSet.has('approved') ? baseCounts.approved : 0,
          rejected: selectedSet.has('rejected') ? baseCounts.rejected : 0,
        });
        return;
      }

      if (hasSelectedStatusFilter) {
        this.statusTileCounts.set({
          underReview: 0,
          approved: 0,
          rejected: 0,
        });
        return;
      }

      this.statusTileCounts.set(baseCounts);
    } finally {
      if (requestId === this.statusTileCountsRequestId) {
        this.isStatusTileCountsLoading.set(false);
      }
    }
  }

  getStatusClass(status: RmaSummaryRow['status']): string {
    const normalized = (status || '').replace(/[^a-zA-Z]/g, '').toLowerCase();

    if (normalized.includes('reject')) {
      return 'status-pill rejected';
    }

    if (
      normalized.includes('approved') ||
      normalized.includes('done') ||
      normalized.includes('complete')
    ) {
      return 'status-pill approved';
    }

    if (normalized.includes('created')) {
      return 'status-pill created';
    }

    return 'status-pill pending';
  }

  getComplianceClass(compliance: string): string {
    const normalized = (compliance || '').replace(/[^a-zA-Z]/g, '').toLowerCase();

    if (normalized.includes('noncompliant') || normalized.includes('non')) {
      return 'compliance-pill non-compliant';
    }

    if (normalized.includes('compliant')) {
      return 'compliance-pill compliant';
    }

    return 'compliance-pill pending';
  }

  getTodaysDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

